import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../../components/ui/sheet";
import { Calendar as CalendarPicker } from "../../../components/ui/calendar";
import {
  ArrowLeft,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  Gauge,
  MapPin,
  Minus,
  Route,
  Navigation,
  Pause,
  Play,
  Plus,
} from "lucide-react";
import { useApi } from "../../../hooks/useApi";
import { toast } from "react-toastify";
import { isAfter, startOfDay } from "date-fns";

/** Default center [lng, lat] — same pattern as SingleVehicleMap before route loads */
const DEFAULT_CENTER = [78.486671, 17.385044];

/** Match MapplsMap.js showSpecificPointOnMap marker icons */
const START_MARKER_ICON = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 64 64">
    <circle cx="32" cy="32" r="28" fill="#22c55e" stroke="white" stroke-width="4"/>
    <text x="32" y="40" font-size="22" fill="white" font-family="Arial" text-anchor="middle" font-weight="bold">S</text>
  </svg>
`)}`;

const END_MARKER_ICON = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 64 64">
    <circle cx="32" cy="32" r="28" fill="#ef4444" stroke="white" stroke-width="4"/>
    <text x="32" y="40" font-size="22" fill="white" font-family="Arial" text-anchor="middle" font-weight="bold">E</text>
  </svg>
`)}`;

const DEBUG_PLAYBACK_FIT = process.env.NODE_ENV !== "production";
const DEBUG_USER_ZOOM_LOG = process.env.NODE_ENV !== "production";
let playbackFitRunId = 0;

// Same Mappls reverse-geocode setup as LatLngLocationPage.js
const MAPPLS_ACCESS_TOKEN = "bb455d19ed3189e52f199c00f82ea641";

function isFiniteNumber(n) {
  return typeof n === "number" && Number.isFinite(n);
}

function addressCacheKey(lat, lng) {
  // Round to reduce duplicate calls from tiny GPS jitter
  return `${lat.toFixed(5)},${lng.toFixed(5)}`;
}

async function reverseGeocodeMappls({ lat, lng, signal }) {
  const url = new URL(
    `https://apis.mappls.com/advancedmaps/v1/${encodeURIComponent(MAPPLS_ACCESS_TOKEN)}/rev_geocode`
  );
  url.searchParams.set("lat", String(lat));
  url.searchParams.set("lng", String(lng));
  url.searchParams.set("region", "IND");

  const res = await fetch(url.toString(), {
    method: "GET",
    signal,
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Mappls reverse geocode failed (${res.status}) ${text}`.trim());
  }

  const data = await res.json();
  if (data?.responseCode && Number(data.responseCode) !== 200) {
    const msg = data?.error || data?.error_description || "Reverse geocode returned an error";
    throw new Error(`Mappls reverse geocode failed: ${msg}`.trim());
  }

  const first = Array.isArray(data?.results) ? data.results[0] : null;
  return {
    poi: first?.poi || "",
    formattedAddress: first?.formatted_address || first?.formattedAddress || "",
  };
}

function getMapZoomSafe(map) {
  try {
    if (!map || typeof map.getZoom !== "function") return null;
    const z = map.getZoom();
    return typeof z === "number" && Number.isFinite(z) ? z : null;
  } catch (_) {
    return null;
  }
}

function logZoomChange({ label, runId, before, after, details }) {
  if (!DEBUG_PLAYBACK_FIT) return;
  if (before == null || after == null) return;
  const delta = after - before;
  const dir = delta > 0.02 ? "zoom-in" : delta < -0.02 ? "zoom-out" : "no-change";
  
}

function scheduleZoomChecks(map, { label, runId, beforeZoom, delays = [160, 600], details }) {
  if (!DEBUG_PLAYBACK_FIT) return;
  for (const d of delays) {
    setTimeout(() => {
      const afterZoom = getMapZoomSafe(map);
      logZoomChange({ label, runId, before: beforeZoom, after: afterZoom, details });
    }, d);
  }
}

function escapeHtml(s) {
  if (s == null || s === "") return "N/A";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatDateTime(dateString) {
  if (!dateString) return "N/A";
  try {
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "N/A";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    const h = String(d.getHours()).padStart(2, "0");
    const m = String(d.getMinutes()).padStart(2, "0");
    const sec = String(d.getSeconds()).padStart(2, "0");
    return `${day}/${month}/${year} ${h}:${m}:${sec}`;
  } catch {
    return "N/A";
  }
}

function formatDateShort(dateString) {
  if (!dateString) return "N/A";
  try {
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "N/A";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return "N/A";
  }
}

/** e.g. "10:59 AM" for timeline rows */
function formatTimeAmPm(dateString) {
  if (!dateString) return "—";
  try {
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "—";
  }
}

/** Human duration: "3m 19s", "18h 2m 45s" */
function formatDurationHuman(ms) {
  if (ms == null || Number.isNaN(ms) || ms < 0) return "—";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h < 24) return mm > 0 ? `${h}h ${mm}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const hh = h % 24;
  return hh > 0 ? `${d}d ${hh}h` : `${d}d`;
}

function tripOrdinal(n) {
  if (n % 10 === 1 && n % 100 !== 11) return `${n}st`;
  if (n % 10 === 2 && n % 100 !== 12) return `${n}nd`;
  if (n % 10 === 3 && n % 100 !== 13) return `${n}rd`;
  return `${n}th`;
}

function pointLatLngNum(p) {
  const lat = Number(p?.latitude ?? p?.lat);
  const lng = Number(p?.longitude ?? p?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function formatCoordAddress(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "Location unknown";
  return `${lat.toFixed(5)}°, ${lng.toFixed(5)}°`;
}

function getDistance(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function formatKmToHuman(km) {
  if (km == null || !Number.isFinite(km) || km < 0) return "—";
  if (km < 0.001) return `${Math.round(km * 1000)} m`;
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${km.toFixed(0)} km`;
}

/**
 * Group consecutive points that are within ~same location (stationary / GPS noise).
 * Returns clusters with indices into the original sorted `points` array.
 */
function clusterPlaybackPoints(points, thresholdMeters = 14) {
  if (!Array.isArray(points) || points.length === 0) return [];
  const thresholdKm = thresholdMeters / 1000;
  const clusters = [];
  let start = 0;
  const ll = (i) => pointLatLngNum(points[i]);

  for (let i = 1; i <= points.length; i++) {
    const lastInRun = i - 1;
    const cur = i < points.length ? ll(i) : null;
    const prev = ll(lastInRun);
    if (i < points.length && cur && prev) {
      const d = getDistance(prev.lat, prev.lng, cur.lat, cur.lng);
      if (d < thresholdKm) continue;
    }
    const slice = points.slice(start, i);
    const first = slice[0];
    const last = slice[slice.length - 1];
    const t0 = new Date(first.point_time).getTime();
    const t1 = new Date(last.point_time).getTime();
    let cLat = 0;
    let cLng = 0;
    let n = 0;
    for (const p of slice) {
      const x = pointLatLngNum(p);
      if (x) {
        cLat += x.lat;
        cLng += x.lng;
        n += 1;
      }
    }
    clusters.push({
      startIndex: start,
      endIndex: i - 1,
      points: slice,
      centerLat: n ? cLat / n : null,
      centerLng: n ? cLng / n : null,
      startTime: t0,
      endTime: t1,
    });
    start = i;
  }
  return clusters;
}

function legDistanceAlongPoints(points, fromIdx, toIdx) {
  if (fromIdx >= toIdx || !points?.length) return 0;
  let km = 0;
  for (let j = fromIdx; j < toIdx; j++) {
    const a = pointLatLngNum(points[j]);
    const b = pointLatLngNum(points[j + 1]);
    if (a && b) km += getDistance(a.lat, a.lng, b.lat, b.lng);
  }
  return km;
}

/**
 * Sums Haversine distance per calendar day (local timezone).
 * Each segment i→i+1 is counted on the arrival point's day (point i+1).
 */
function computeDistanceByDay(sortedPoints) {
  if (!sortedPoints?.length) return [];
  const byDay = new Map();
  for (let j = 0; j < sortedPoints.length - 1; j++) {
    const a = pointLatLngNum(sortedPoints[j]);
    const b = pointLatLngNum(sortedPoints[j + 1]);
    if (!a || !b) continue;
    const km = getDistance(a.lat, a.lng, b.lat, b.lng);
    const t = new Date(sortedPoints[j + 1].point_time);
    if (Number.isNaN(t.getTime())) continue;
    const key = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
    byDay.set(key, (byDay.get(key) || 0) + km);
  }
  return Array.from(byDay.entries())
    .map(([dateKey, km]) => {
      const [y, mo, d] = dateKey.split("-").map(Number);
      const localD = new Date(y, mo - 1, d);
      const label = localD.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return { dateKey, label, km };
    })
    .sort((a, b) => a.dateKey.localeCompare(b.dateKey));
}

function formatShortDayLabel(dateString) {
  if (!dateString) return "";
  try {
    const t = new Date(dateString);
    if (Number.isNaN(t.getTime())) return "";
    return t.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function tripStartDate(tr) {
  return (
    tr?.trip_start_time ??
    tr?.startTime ??
    tr?.start_time ??
    tr?.created_at ??
    tr?.createdAt
  );
}

function parseTripDateLoose(dateString) {
  if (!dateString) return null;
  if (dateString instanceof Date) {
    return Number.isNaN(dateString.getTime()) ? null : dateString;
  }
  const s = String(dateString).trim();
  // API often returns "YYYY-MM-DD HH:mm:ss" (non-ISO). Safari may treat it as invalid.
  // Convert to ISO-like "YYYY-MM-DDTHH:mm:ss" for reliable parsing.
  const isoLike = /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/.test(s) ? s.replace(" ", "T") : s;
  const d = new Date(isoLike);
  return Number.isNaN(d.getTime()) ? null : d;
}

function tripDistanceKm(tr) {
  const raw = tr?.distance_km ?? tr?.distanceKm ?? tr?.distance;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function getTripIdValue(tr) {
  return tr?.id || tr?._id || tr?.trip_id || null;
}

function buildSegPointsFromPoints(points) {
  if (!Array.isArray(points) || points.length === 0) return [];
  const latLngs = points.map((p) => pointLatLngNum(p));
  let cumulativeKm = 0;
  const segPoints = [];
  for (let i = 0; i < points.length; i++) {
    const prev = i > 0 ? latLngs[i - 1] : null;
    const curr = latLngs[i];
    if (prev && curr) cumulativeKm += getDistance(prev.lat, prev.lng, curr.lat, curr.lng);
    const kind = i === 0 ? "start" : i === points.length - 1 ? "end" : "point";
    const label = kind === "start" ? "Trip started" : kind === "end" ? "Trip ended" : "Waypoint";
    segPoints.push({
      kind,
      label,
      lat: curr?.lat ?? null,
      lng: curr?.lng ?? null,
      time: points[i]?.point_time,
      distanceKm: kind === "start" ? 0 : cumulativeKm,
    });
  }
  return segPoints;
}

/** Avg daily distance from trips list (/trips). */
function computeAvgDistancePerDay(trips) {
  if (!Array.isArray(trips) || trips.length === 0) return 0;
  const byDay = new Map();
  for (const tr of trips) {
    const s = tripStartDate(tr);
    if (!s) continue;
    const d = parseTripDateLoose(s);
    if (!d) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
    byDay.set(key, (byDay.get(key) || 0) + tripDistanceKm(tr));
  }
  if (byDay.size === 0) return 0;
  let sum = 0;
  byDay.forEach((km) => {
    sum += km;
  });
  return sum / byDay.size;
}

/** Sum trip distance per calendar day from trips list API. */
function computeTripDistanceByDay(trips) {
  if (!Array.isArray(trips) || trips.length === 0) return [];
  const byDay = new Map();
  for (const tr of trips) {
    const s = tripStartDate(tr);
    if (!s) continue;
    const d = parseTripDateLoose(s);
    if (!d) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
    byDay.set(key, (byDay.get(key) || 0) + tripDistanceKm(tr));
  }
  return Array.from(byDay.entries())
    .map(([dateKey, km]) => ({ dateKey, km }))
    .sort((a, b) => a.dateKey.localeCompare(b.dateKey));
}

/**
 * Timeline rows: path start → end only (full point list stays in `points` for map/route).
 */
function buildPlaybackTimeline(points, trip) {
  const sorted = [...(points || [])].sort(
    (a, b) => new Date(a.point_time) - new Date(b.point_time)
  );

  if (!sorted.length) return { points: sorted, clusters: [], items: [], trip };

  const items = [];
  const latLngs = sorted.map((p) => pointLatLngNum(p));

  let cumulativeKm = 0;

  const first = sorted[0];
  const firstLL = latLngs[0];
  items.push({
    kind: "start",
    clusterIndex: 0,
    time: first?.point_time,
    lat: firstLL?.lat ?? null,
    lng: firstLL?.lng ?? null,
    distanceKm: 0,
  });

  for (let i = 1; i < sorted.length - 1; i++) {
    const prev = latLngs[i - 1];
    const curr = latLngs[i];
    if (prev && curr) cumulativeKm += getDistance(prev.lat, prev.lng, curr.lat, curr.lng);

    items.push({
      kind: "point",
      pointIndex: i,
      time: sorted[i]?.point_time,
      lat: curr?.lat ?? null,
      lng: curr?.lng ?? null,
      distanceKm: cumulativeKm,
    });
  }

  if (sorted.length > 1) {
    const last = sorted[sorted.length - 1];
    const lastLL = latLngs[sorted.length - 1];
    const prev = latLngs[sorted.length - 2];
    if (prev && lastLL) cumulativeKm += getDistance(prev.lat, prev.lng, lastLL.lat, lastLL.lng);

    items.push({
      kind: "end",
      clusterIndex: sorted.length - 1,
      time: last?.point_time,
      lat: lastLL?.lat ?? null,
      lng: lastLL?.lng ?? null,
      distanceKm: cumulativeKm,
    });
  }

  return { points: sorted, clusters: [], items, trip };
}

/** Handles various API envelopes: { data: [] }, { trips: [] }, raw array */
function normalizeTripsList(res) {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.trips)) return res.trips;
  if (res.data && Array.isArray(res.data.data)) return res.data.data;
  return [];
}

function waitForMapplsSdk(maxMs = 10000) {
  return new Promise((resolve, reject) => {
    const t0 = Date.now();
    const tick = () => {
      if (
        window.mappls?.Map &&
        window.mappls?.Marker &&
        window.mappls?.Polyline &&
        window.mappls?.LatLngBounds
      ) {
        resolve();
        return;
      }
      if (Date.now() - t0 > maxMs) {
        reject(new Error("Mappls SDK not ready"));
        return;
      }
      setTimeout(tick, 50);
    };
    tick();
  });
}

function pointToLatLng(p) {
  if (!p || typeof p !== "object") return null;
  const lat = Number(
    p.latitude ?? p.lat ?? p?.location?.latitude ?? p?.location?.lat ?? p?.geo_location?.lat
  );
  const lng = Number(
    p.longitude ??
      p.lng ??
      p?.location?.longitude ??
      p?.location?.lng ??
      p?.geo_location?.lng
  );
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  const coords = p.coordinates;
  if (Array.isArray(coords) && coords.length >= 2) {
    const la = Number(coords[1]);
    const ln = Number(coords[0]);
    if (Number.isFinite(la) && Number.isFinite(ln)) return { lat: la, lng: ln };
  }
  return null;
}

/** Same shape as MapplsMap / LiveMap: [{ lat, lng }, ...] from API points */
function buildRouteCoordinates(pointsIn, tripData) {
  const out = [];
  if (Array.isArray(pointsIn)) {
    for (const p of pointsIn) {
      const ll = pointToLatLng(p);
      if (ll) out.push(ll);
    }
  }
  if (out.length >= 2) return out;
  const t = tripData || {};
  const sLat = Number(t.start_lat);
  const sLng = Number(t.start_lng);
  const eLat = Number(t.end_lat);
  const eLng = Number(t.end_lng);
  if (
    [sLat, sLng, eLat, eLng].every(Number.isFinite) &&
    (sLat !== eLat || sLng !== eLng)
  ) {
    return [
      { lat: sLat, lng: sLng },
      { lat: eLat, lng: eLng },
    ];
  }
  return out;
}

/** Largest lat/lng span of the path bounding box (degrees). */
function getPathBoundingSpan(path) {
  if (!path?.length) return 0;
  const swLat = Math.min(...path.map((p) => p.lat));
  const neLat = Math.max(...path.map((p) => p.lat));
  const swLng = Math.min(...path.map((p) => p.lng));
  const neLng = Math.max(...path.map((p) => p.lng));
  return Math.max(Math.abs(neLat - swLat), Math.abs(neLng - swLng));
}

/**
 * Padding + maxZoom — aligned with DayTripsPlaybackHistory.fitViewport (padding 100, maxZoom 14).
 */
function getPlaybackFitBoundsOptions(_maxSpan) {
  return { padding: 100, maxZoom: 10, duration: 1000 };
}

/** Subsample path for fitBounds (Mappls can throw on very large paths). Same cap as DayTripsPlaybackHistory. */
function getFitBoundsPath(path) {
  if (!path?.length) return [];
  const normalized = path.filter(
    (p) =>
      p &&
      Number.isFinite(p.lat) &&
      Number.isFinite(p.lng) &&
      p.lat >= -90 &&
      p.lat <= 90 &&
      p.lng >= -180 &&
      p.lng <= 180
  );
  if (!normalized.length) return [];
  const MAX_FIT_POINTS = 600;
  const step = Math.max(1, Math.ceil(normalized.length / MAX_FIT_POINTS));
  const fitPath = normalized.filter((_, idx) => idx % step === 0);
  if (fitPath[fitPath.length - 1] !== normalized[normalized.length - 1]) {
    fitPath.push(normalized[normalized.length - 1]);
  }
  return fitPath;
}

function fitPlaybackViewport(map, path, opts = {}) {
  if (!map || !path?.length) return;
  const mid = path[Math.floor(path.length / 2)] ?? path[0];

  const runId = ++playbackFitRunId;
  const zoomBefore = getMapZoomSafe(map);
  const runResize = () => {
    try {
      if (typeof map.resize === "function") map.resize();
    } catch (_) {}
  };

  const maxSpan = getPathBoundingSpan(path);
  const tiny = maxSpan < 1e-8;

  const containerEl =
    typeof document !== "undefined"
      ? document.getElementById("playback-map-container")
      : null;
  const containerRect = containerEl?.getBoundingClientRect?.() ?? null;

  const minLat = Math.min(...path.map((p) => p.lat));
  const maxLat = Math.max(...path.map((p) => p.lat));
  const minLng = Math.min(...path.map((p) => p.lng));
  const maxLng = Math.max(...path.map((p) => p.lng));
  const fitOpts = getPlaybackFitBoundsOptions(maxSpan);
  const sheetOpen = Boolean(opts.sheetOpen);
  // Keep fitBounds options identical to MapplsMap.
  // We only pan up after fitBounds to avoid the bottom sheet covering the route.

  const getBottomOverlayHeightPx = () => {
    if (typeof document === "undefined") return 0;
    if (!sheetOpen) return 0;
    // Dialog container used in JSX below.
    const sheetEl = document.querySelector('[aria-labelledby="playback-timeline-title"]');
    const rect = sheetEl?.getBoundingClientRect?.();
    return rect?.height || 0;
  };

  const panRouteUpByBottomOverlay = () => {
    if (typeof map.setCenter !== "function") return;
    // Prefer current zoom if available; otherwise just skip shifting.
    let z = null;
    try {
      z = typeof map.getZoom === "function" ? map.getZoom() : null;
    } catch (_) {}
    if (typeof z !== "number" || !Number.isFinite(z)) return;

    const overlayH = getBottomOverlayHeightPx();
    if (!overlayH) return;

    // Move content away from the covered bottom area.
    // (Uniform padding shrinks the route; pan shifting keeps zoom stable.)
    const shiftPx = Math.min(overlayH * 0.2, 180);

    // Approximate meters per pixel at this zoom (WebMercator-style).
    // Good enough for UI offsetting.
    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;
    const latRad = (centerLat * Math.PI) / 180;
    const mPerPx = (156543.03392 * Math.cos(latRad)) / Math.pow(2, z);
    const metersToDegreesLat = 1 / 111320;
    const deltaLat = shiftPx * mPerPx * metersToDegreesLat;

    // Pan "south" so route moves up on screen.
    map.setCenter([centerLng, centerLat - deltaLat]);
  };

  if (DEBUG_PLAYBACK_FIT) {
    let zoomNow = null;
    try {
      zoomNow = typeof map.getZoom === "function" ? map.getZoom() : null;
    } catch (_) {}
    
  }

  const applyBoundsFit = () => {
    if (!path?.length) return;
    try {
      if (tiny) {
        if (typeof map.setCenter === "function") map.setCenter([mid.lng, mid.lat]);
        if (typeof map.setZoom === "function") map.setZoom(18);
        return;
      }

      if (window.mappls?.LatLngBounds) {
        const fitBoundsPath = getFitBoundsPath(path);
        if (!fitBoundsPath.length) return;

        const bounds = new window.mappls.LatLngBounds();
        if (fitBoundsPath.length === 1) {
          const d = Math.max(maxSpan * 0.5, 0.002);
          bounds.extend({ lat: fitBoundsPath[0].lat - d, lng: fitBoundsPath[0].lng - d });
          bounds.extend({ lat: fitBoundsPath[0].lat + d, lng: fitBoundsPath[0].lng + d });
        } else {
          fitBoundsPath.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
        }

        map.fitBounds(bounds, fitOpts);
        // Mappls fitBounds zoom is often async; sample shortly after.
        scheduleZoomChecks(map, {
          label: "fitBounds",
          runId,
          beforeZoom: zoomBefore,
          details: { padding: fitOpts?.padding, maxZoom: fitOpts?.maxZoom },
        });
        // After fitBounds, shift the viewport up to account for bottom overlays.
        setTimeout(panRouteUpByBottomOverlay, 160);
      } else if (typeof map.setCenter === "function") {
        map.setCenter([mid.lng, mid.lat]);
        if (typeof map.setZoom === "function") {
          const { maxZoom } = fitOpts;
          map.setZoom(maxZoom);
        }
        scheduleZoomChecks(map, {
          label: "setZoom(centerFallback)",
          runId,
          beforeZoom: zoomBefore,
          details: { maxZoom: fitOpts?.maxZoom },
        });
        setTimeout(panRouteUpByBottomOverlay, 160);
      }
    } catch (_) {
      try {
        if (typeof map.setCenter === "function") map.setCenter([mid.lng, mid.lat]);
        if (typeof map.setZoom === "function") {
          map.setZoom(fitOpts.maxZoom);
        }
      } catch (_) {}
    }
  };

  // Resize first so fitBounds uses the real map container size (critical on mobile / flex).
  runResize();
  applyBoundsFit();
  // Refit after layout / fonts — first fitBounds often runs with stale canvas dimensions.
  setTimeout(() => {
    runResize();
    applyBoundsFit();
  }, 200);
  setTimeout(() => {
    runResize();
    applyBoundsFit();
  }, 550);

  runResize();
  setTimeout(runResize, 250);
}

/** Same red car SVG data URL as MapplsMap.js (getCarIconRed) */
function getCarIconRed() {
  const CarIcon = `
  <svg width="64" height="128" viewBox="0 0 300 600" xmlns="http://www.w3.org/2000/svg">
    <rect x="80" y="120" width="140" height="300" rx="40" fill="#dc2626"/>
    <rect x="105" y="70" width="90" height="70" rx="30" fill="#b91c1c"/>
    <rect x="110" y="90" width="80" height="40" rx="10" fill="#e0f2fe"/>
    <rect x="110" y="300" width="80" height="50" rx="10" fill="#e0f2fe"/>
    <rect x="100" y="110" width="30" height="10" rx="5" fill="#fde047"/>
    <rect x="170" y="110" width="30" height="10" rx="5" fill="#fde047"/>
    <rect x="100" y="400" width="30" height="10" rx="5" fill="#7f1d1d"/>
    <rect x="170" y="400" width="30" height="10" rx="5" fill="#7f1d1d"/>
    <rect x="60" y="170" width="20" height="60" rx="8" fill="#111827"/>
    <rect x="60" y="310" width="20" height="60" rx="8" fill="#111827"/>
    <rect x="220" y="170" width="20" height="60" rx="8" fill="#111827"/>
    <rect x="220" y="310" width="20" height="60" rx="8" fill="#111827"/>
    <line x1="150" y1="60" x2="150" y2="460" stroke="#ffffff" stroke-width="4" stroke-dasharray="12,12" opacity="0.6"/>
  </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(CarIcon)}`;
}

function calculateBearing(lat1, lng1, lat2, lng2) {
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const lat1Rad = (lat1 * Math.PI) / 180;
  const lat2Rad = (lat2 * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2Rad);
  const x =
    Math.cos(lat1Rad) * Math.sin(lat2Rad) -
    Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);
  const bearing = (Math.atan2(y, x) * 180) / Math.PI;
  return (bearing + 360) % 360;
}

function lerpAngle(current, target, factor) {
  let diff = target - current;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  return current + diff * factor;
}

function hasSetRotation(marker) {
  return marker && typeof marker.setRotation === "function";
}

function buildEndpointPopupHtml({ title, titleColor, point, trip, tripIdLabel }) {
  const t = trip || {};
  return `
    <div style="padding:6px;min-width:230px;max-width:230px;max-height:400px;overflow-y:auto;">
      <h3 style="margin:0 0 10px;font-size:13px;font-weight:600;color:${titleColor};border-bottom:2px solid ${titleColor};padding-bottom:6px;">${title}</h3>
      <div style="margin-bottom:10px;padding:6px;">
        <p style="margin:3px 0;font-size:10px;color:#374151;font-weight:600;">TRIP INFORMATION</p>
        <p style="margin:1px 0;font-size:10px;color:#374151;"><b>Trip ID:</b> ${escapeHtml(t.id ?? t._id ?? tripIdLabel)}</p>
        <p style="margin:1px 0;font-size:10px;color:#374151;"><b>Status:</b> ${escapeHtml(t.trip_status)}</p>
        <p style="margin:1px 0;font-size:10px;color:#374151;"><b>Start:</b> ${escapeHtml(formatDateTime(t.trip_start_time))}</p>
        <p style="margin:1px 0;font-size:10px;color:#374151;"><b>End:</b> ${escapeHtml(formatDateTime(t.trip_end_time))}</p>
        <p style="margin:1px 0;font-size:10px;color:#374151;"><b>Duration:</b> ${escapeHtml(t.duration_minutes)} min</p>
        <p style="margin:1px 0;font-size:10px;color:#374151;"><b>Distance:</b> ${t.distance_km != null ? Number(t.distance_km).toFixed(2) : "N/A"} km</p>
        ${t.created_at ? `<p style="margin:1px 0;font-size:10px;color:#374151;"><b>Created:</b> ${escapeHtml(formatDateShort(t.created_at))}</p>` : ""}
        <p style="margin:1px 0;font-size:10px;color:#374151;"><b>Terminal ID:</b> ${escapeHtml(t.device_id ?? point?.terminal_id)}</p>
        <p style="margin:1px 0;font-size:10px;color:#374151;"><b>Speed:</b> ${escapeHtml(point?.speed ?? 0)} km/h</p>
        <p style="margin:1px 0;font-size:10px;color:#374151;"><b>Point Time:</b> ${escapeHtml(formatDateTime(point?.point_time))}</p>
        <p style="margin:1px 0;font-size:10px;color:#6b7280;"><b>Coords:</b> ${point?.latitude != null ? Number(point.latitude).toFixed(6) : ""}, ${point?.longitude != null ? Number(point.longitude).toFixed(6) : ""}</p>
      </div>
    </div>
  `;
}

export default function PlaybackHistory() {
  const navigate = useNavigate();
  const { deviceId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { get } = useApi();
  const tripId = searchParams.get("tripId");
  const mapBasePath = location.pathname.startsWith("/customer/") ? "/customer" : "/admin";

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const startMarkerRef = useRef(null);
  const endMarkerRef = useRef(null);
  const polylineRef = useRef(null);
  const setupPlaybackRouteRef = useRef(null);
  // When user presses Play, keep the full route polyline visible.
  // Some effects may re-run `setupPlaybackRoute()` in endpoints-only mode; this
  // prevents them from removing/reverting the full route until animation stops.
  const isFullRouteRequestedRef = useRef(false);
  const pointsRef = useRef([]);
  const tripRef = useRef(null);
  const isMapLoadedRef = useRef(false);
  const animationMarkerRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  const animationStartTimeoutRef = useRef(null);
  const isAnimatingRef = useRef(false);

  const [points, setPoints] = useState([]);
  const [trip, setTrip] = useState(null);
  const [tripList, setTripList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const playbackSpeedRef = useRef(1);
  playbackSpeedRef.current = playbackSpeed;
  const [timelineSheetOpen, setTimelineSheetOpen] = useState(false);
  const [calendarSheetOpen, setCalendarSheetOpen] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(() => new Date());
  const [expandedTrips, setExpandedTrips] = useState({});
  const [tripPointsById, setTripPointsById] = useState({});
  const [tripPointsLoadingById, setTripPointsLoadingById] = useState({});

  // Reverse-geocode cache for timeline labels (lat/lng -> address)
  const addressCacheRef = useRef(new Map());
  const addressPendingRef = useRef(new Set());
  const addressQueueRef = useRef([]);
  const addressInFlightRef = useRef(0);
  const [addressCacheVersion, setAddressCacheVersion] = useState(0);

  const queueReverseGeocode = useCallback((lat, lng) => {
    if (!isFiniteNumber(lat) || !isFiniteNumber(lng)) return;
    const key = addressCacheKey(lat, lng);
    if (addressCacheRef.current.has(key)) return;
    if (addressPendingRef.current.has(key)) return;

    addressPendingRef.current.add(key);
    addressQueueRef.current.push({ key, lat, lng });

    const pump = () => {
      // small concurrency limit so we don't spam 1000+ calls
      const MAX = 4;
      while (addressInFlightRef.current < MAX && addressQueueRef.current.length) {
        const job = addressQueueRef.current.shift();
        if (!job) break;
        addressInFlightRef.current += 1;

        const controller = new AbortController();
        reverseGeocodeMappls({ lat: job.lat, lng: job.lng, signal: controller.signal })
          .then((res) => {
            const label = res?.poi || res?.formattedAddress || "";
            addressCacheRef.current.set(job.key, { ok: true, label });
          })
          .catch((err) => {
            addressCacheRef.current.set(job.key, {
              ok: false,
              label: "",
              error: err?.message || "Reverse geocode failed",
            });
          })
          .finally(() => {
            addressInFlightRef.current -= 1;
            addressPendingRef.current.delete(job.key);
            setAddressCacheVersion((v) => v + 1);
            pump();
          });
      }
    };

    pump();
  }, []);

  function AddressLabel({ lat, lng }) {
    const can = isFiniteNumber(lat) && isFiniteNumber(lng);
    const key = can ? addressCacheKey(lat, lng) : "";
    // depend on version so we re-read cache after updates
    // eslint-disable-next-line no-unused-vars
    const _v = addressCacheVersion;
    const cached = can ? addressCacheRef.current.get(key) : null;

    useEffect(() => {
      if (!can) return;
      if (cached?.ok && cached?.label) return;
      // Kick off reverse-geocode lazily when the row is mounted.
      queueReverseGeocode(lat, lng);
    }, [can, key, lat, lng, cached?.ok, cached?.label, queueReverseGeocode]);

    return cached?.ok && cached?.label ? cached.label : formatCoordAddress(lat, lng);
  }

  const timelineModel = useMemo(
    () => buildPlaybackTimeline(points, trip),
    [points, trip]
  );

  // Reset expanded state whenever the points timeline changes.
  useEffect(() => {
    setExpandedTrips({});
  }, [points]);

  const tripCards = useMemo(() => {
    if (!Array.isArray(tripList) || tripList.length === 0) return [];
    const activeTripId = getTripIdValue(trip);
    const activeSegPoints = buildSegPointsFromPoints(points);

    return tripList.map((tr, idx) => {
      const trId = getTripIdValue(tr) || `trip-${idx + 1}`;
      const startTime = tr?.trip_start_time ?? tr?.start_time ?? tr?.startTime ?? tr?.created_at;
      const endTime = tr?.trip_end_time ?? tr?.end_time ?? tr?.endTime ?? tr?.updated_at ?? startTime;
      const isActive = activeTripId && trId === activeTripId;
      const cachedSegPoints = tripPointsById[trId] || [];
      return {
        tripId: trId,
        tripNum: idx + 1,
        startTime,
        endTime,
        points: isActive ? activeSegPoints : cachedSegPoints,
        isActive,
        isLoadingPoints: Boolean(tripPointsLoadingById[trId]),
      };
    });
  }, [tripList, trip, points, tripPointsById, tripPointsLoadingById]);

  // Trip cards are intentionally collapsed by default.
  // User can expand them by tapping the trip header row.

  const distanceByDay = useMemo(
    () => computeDistanceByDay(timelineModel.points),
    [timelineModel.points]
  );
  const tripSpansMultipleDays = distanceByDay.length > 1;

  const tripRuntimeLabel = useMemo(() => {
    if (!trip?.trip_start_time || !trip?.trip_end_time) return "—";
    const ms = new Date(trip.trip_end_time) - new Date(trip.trip_start_time);
    return formatDurationHuman(ms);
  }, [trip]);

  const avgDistancePerDayLabel = useMemo(() => {
    // Prefer the current trip's day-wise distance (from points timeline).
    if (distanceByDay.length > 0) {
      const sum = distanceByDay.reduce((acc, r) => acc + (Number(r?.km) || 0), 0);
      const avg = sum / distanceByDay.length;
      return `${avg.toFixed(3)} km/day`;
    }
    // Fallback: average from trips list history (if available).
    const avg = computeAvgDistancePerDay(tripList);
    return `${avg.toFixed(2)} km/day`;
  }, [distanceByDay, tripList]);

  const tripDistanceByDay = useMemo(() => computeTripDistanceByDay(tripList), [tripList]);
  const tripDistanceByDayMap = useMemo(() => {
    const m = new Map();
    for (const row of tripDistanceByDay) m.set(row.dateKey, row.km);
    return m;
  }, [tripDistanceByDay]);

  /** No future days — trips do not exist for dates after today. */
  const disableDaysAfterToday = useCallback((date) => {
    return isAfter(startOfDay(date), startOfDay(new Date()));
  }, []);

  const focusMapOn = useCallback((lat, lng) => {
    const map = mapInstanceRef.current;
    if (!map || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    try {
      if (typeof map.panTo === "function") {
        map.panTo({ lat, lng }, { duration: 600 });
      } else if (typeof map.setCenter === "function") {
        map.setCenter([lng, lat]);
      }
    } catch (_) {}
  }, []);

  const openLatLngDetails = useCallback(
    (lat, lng) => {
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const base = location.pathname.replace(/\/map\/playback\/[^/]+$/, "");
      navigate(
        `${base}/map/location?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`
      );
    },
    [location.pathname, navigate]
  );

  const stopPlaybackAnimation = useCallback(() => {
    isAnimatingRef.current = false;
    setIsAnimating(false);
    if (animationStartTimeoutRef.current) {
      clearTimeout(animationStartTimeoutRef.current);
      animationStartTimeoutRef.current = null;
    }
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }
    if (animationMarkerRef.current) {
      try {
        if (typeof animationMarkerRef.current.remove === "function") {
          animationMarkerRef.current.remove();
        }
      } catch (_) {}
      animationMarkerRef.current = null;
    }
  }, []);

  const loadTripOnMap = useCallback(
    async (tripLike) => {
      const selectedId = getTripIdValue(tripLike);
      if (!selectedId) {
        toast.error("Trip ID not found");
        return;
      }

      try {
        setIsLoading(true);
        // Close timeline so user can immediately see the selected trip on map.
        setTimelineSheetOpen(false);
        isFullRouteRequestedRef.current = false;
        stopPlaybackAnimation();
        const response = await get(`/trips/${encodeURIComponent(selectedId)}`);
        const tripData = response?.trip || null;
        const pts = Array.isArray(response?.points) ? response.points : [];
        if (!tripData || pts.length === 0) throw new Error("Playback points not found");
        const sorted = [...pts].sort((a, b) => new Date(a.point_time) - new Date(b.point_time));
        tripRef.current = tripData;
        setTrip(tripData);
        setPoints(sorted);

        // Draw selected trip route immediately on current map instance.
        const map = mapInstanceRef.current;
        const setupRoute = setupPlaybackRouteRef.current;
        if (map && typeof setupRoute === "function") {
          setupRoute(map, sorted, tripData, { endpointsOnly: false });
        }
      } catch (err) {
        toast.error(err?.message || "Failed to load selected trip");
      } finally {
        setIsLoading(false);
      }
    },
    [get, stopPlaybackAnimation]
  );

  const toggleTripExpand = useCallback(
    async (seg, isExpanded) => {
      const nextExpanded = !isExpanded;
      setExpandedTrips((prev) => ({ ...prev, [seg.tripId]: nextExpanded }));
      if (!nextExpanded) return;
      if (seg.points.length > 0) return;
      if (tripPointsLoadingById[seg.tripId]) return;
      if (!seg.tripId) return;

      try {
        setTripPointsLoadingById((prev) => ({ ...prev, [seg.tripId]: true }));
        const response = await get(`/trips/${encodeURIComponent(seg.tripId)}`);
        const pts = Array.isArray(response?.points) ? response.points : [];
        const sorted = [...pts].sort((a, b) => new Date(a.point_time) - new Date(b.point_time));
        setTripPointsById((prev) => ({ ...prev, [seg.tripId]: buildSegPointsFromPoints(sorted) }));
      } catch {
        toast.error("Failed to load trip timeline");
      } finally {
        setTripPointsLoadingById((prev) => ({ ...prev, [seg.tripId]: false }));
      }
    },
    [get, tripPointsLoadingById]
  );

  const tripTitle =
    trip?.vehicle_name ||
    (trip?.device_id ? `Device ${trip.device_id}` : null) ||
    (deviceId ? `Device ${deviceId}` : "Playback");
  const tripSubtitle = trip?.trip_start_time ? formatTimeAmPm(trip.trip_start_time) : "";
  const distanceLabel =
    trip?.distance_km != null ? `${Number(trip.distance_km).toFixed(1)} km` : "—";

  // Keep refs in sync every render so map.on("load") / async callbacks always see latest trip points
  // (useEffect runs too late — load often fires before pointsRef was updated).
  pointsRef.current = points;
  tripRef.current = trip;

  /** Same rAF + Haversine path interpolation as MapplsMap.js startAnimation */
  const startPlaybackAnimation = useCallback(
    (speed = "fast") => {
      const map = mapInstanceRef.current;
      if (!map || !isMapLoadedRef.current) {
        toast.error("Map not ready");
        return;
      }
      const pts = pointsRef.current;
      const tripData = tripRef.current;
      const path = buildRouteCoordinates(pts, tripData);
      if (path.length < 2) {
        toast.error("Need at least 2 points for animation");
        return;
      }

      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
      if (animationStartTimeoutRef.current) {
        clearTimeout(animationStartTimeoutRef.current);
        animationStartTimeoutRef.current = null;
      }
      if (animationMarkerRef.current) {
        try {
          if (typeof animationMarkerRef.current.remove === "function") {
            animationMarkerRef.current.remove();
          }
        } catch (_) {}
        animationMarkerRef.current = null;
      }

      isAnimatingRef.current = true;
      setIsAnimating(true);

      const truckIcon = getCarIconRed();
      const startLat = path[0].lat;
      const startLng = path[0].lng;

      try {
        animationMarkerRef.current = new window.mappls.Marker({
          map,
          position: { lat: startLat, lng: startLng },
          icon: truckIcon,
          width: 30,
          height: 60,
          fitbounds: false,
          anchor: { x: 15, y: 60 },
          zIndex: 1000,
        });
        setTimeout(() => {
          try {
            const el = animationMarkerRef.current?.getElement?.();
            if (el) {
              el.style.transition = "transform 0.1s ease-out";
              el.style.zIndex = "1000";
              el.classList.add("animation-car-marker");
            }
          } catch (_) {}
        }, 100);
      } catch (err) {
        isAnimatingRef.current = false;
        setIsAnimating(false);
        return;
      }

      const points = path.map((p) => ({ lat: p.lat, lng: p.lng }));
      const distances = [0];
      let totalDistance = 0;
      for (let i = 0; i < points.length - 1; i++) {
        const d = getDistance(
          points[i].lat,
          points[i].lng,
          points[i + 1].lat,
          points[i + 1].lng
        );
        totalDistance += d;
        distances.push(totalDistance);
      }

      const mult = Math.max(0.25, Math.min(4, playbackSpeedRef.current || 1));
      const baseDuration = speed === "fast" ? 15000 : 30000;
      const animationDuration = baseDuration / mult;

      const pathSpan = getPathBoundingSpan(points);
      const playbackFit = getPlaybackFitBoundsOptions(pathSpan);
      let animationStartDelayMs = 0;

      try {
        if (window.mappls?.LatLngBounds) {
          const zoomBeforeAnim = getMapZoomSafe(map);
          const fitAnimPath = getFitBoundsPath(points);
          const boundsPts = fitAnimPath.length ? fitAnimPath : points;
          const bounds = new window.mappls.LatLngBounds();
          boundsPts.forEach((pt) => bounds.extend({ lat: pt.lat, lng: pt.lng }));
          map.fitBounds(bounds, {
            padding: playbackFit.padding,
            maxZoom: playbackFit.maxZoom,
            duration: Math.max(playbackFit.duration, 1000),
          });
          animationStartDelayMs = Math.max(playbackFit.duration, 1000) + 120;

          scheduleZoomChecks(map, {
            label: "animation.fitBounds",
            runId: 0,
            beforeZoom: zoomBeforeAnim,
            details: {
              sheetOpen: timelineSheetOpen,
              padding: playbackFit.padding,
              maxZoom: playbackFit.maxZoom,
              duration: Math.max(playbackFit.duration, 1000),
            },
          });

          // If the timeline sheet is open, keep zoom stable and just shift up.
          setTimeout(() => {
            if (!timelineSheetOpen) return;
            if (typeof map.setCenter !== "function") return;
            let z = null;
            try {
              z = typeof map.getZoom === "function" ? map.getZoom() : null;
            } catch (_) {}
            if (typeof z !== "number" || !Number.isFinite(z)) return;

            // Prefer the expanded dialog sheet.
            const sheetEl = document.querySelector('[aria-labelledby="playback-timeline-title"]');
            const rect = sheetEl?.getBoundingClientRect?.();
            if (!rect?.height) return;

            const shiftPx = Math.min(rect.height * 0.2, 180);
            const minLat = Math.min(...points.map((p) => p.lat));
            const maxLat = Math.max(...points.map((p) => p.lat));
            const minLng = Math.min(...points.map((p) => p.lng));
            const maxLng = Math.max(...points.map((p) => p.lng));

            const centerLat = (minLat + maxLat) / 2;
            const centerLng = (minLng + maxLng) / 2;
            const latRad = (centerLat * Math.PI) / 180;
            const mPerPx =
              (156543.03392 * Math.cos(latRad)) / Math.pow(2, z);
            const metersToDegreesLat = 1 / 111320;
            const deltaLat = shiftPx * mPerPx * metersToDegreesLat;
            map.setCenter([centerLng, centerLat - deltaLat]);
          }, 160);
        }
      } catch (_) {}

      let startTime = null;
      let currentRotation = 0;
      if (points.length > 1) {
        currentRotation =
          calculateBearing(
            points[0].lat,
            points[0].lng,
            points[1].lat,
            points[1].lng
          ) + 180;
      }

      const animate = (timestamp) => {
        if (!isAnimatingRef.current || !animationMarkerRef.current) return;

        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;

        if (elapsed > animationDuration) {
          // Playback finished; allow setupPlaybackRoute to go back to endpoints-only mode.
          isFullRouteRequestedRef.current = false;
          stopPlaybackAnimation();
          toast.success(`Animation completed! ${points.length} waypoints.`, {
            autoClose: 3000,
          });
          return;
        }

        const targetDist =
          totalDistance > 0 ? (elapsed / animationDuration) * totalDistance : 0;

        let segmentIndex = 0;
        for (let i = 0; i < distances.length - 1; i++) {
          if (targetDist >= distances[i] && targetDist < distances[i + 1]) {
            segmentIndex = i;
            break;
          }
        }
        if (targetDist >= distances[distances.length - 1]) {
          segmentIndex = Math.max(0, distances.length - 2);
        }

        const segmentStartDist = distances[segmentIndex];
        const segmentEndDist = distances[segmentIndex + 1];
        const segmentLen = segmentEndDist - segmentStartDist;
        let t = 0;
        if (segmentLen > 0) {
          t = (targetDist - segmentStartDist) / segmentLen;
        }

        const p1 = points[segmentIndex];
        const p2 = points[segmentIndex + 1];
        const lat = p1.lat + (p2.lat - p1.lat) * t;
        const lng = p1.lng + (p2.lng - p1.lng) * t;

        let targetBearing = currentRotation;
        if (
          Math.abs(p2.lat - p1.lat) > 0.000001 ||
          Math.abs(p2.lng - p1.lng) > 0.000001
        ) {
          const rawBearing = calculateBearing(p1.lat, p1.lng, p2.lat, p2.lng);
          targetBearing = rawBearing + 180;
        }
        currentRotation = lerpAngle(currentRotation, targetBearing, 0.1);

        const marker = animationMarkerRef.current;
        if (marker) {
          if (typeof marker.setPosition === "function") {
            marker.setPosition({ lat, lng });
          } else if (typeof marker.setLatLng === "function") {
            marker.setLatLng([lat, lng]);
          }

          if (hasSetRotation(marker)) {
            marker.setRotation(currentRotation);
          } else {
            const element = marker.getElement ? marker.getElement() : null;
            if (element) {
              const img = element.querySelector("img");
              if (img) img.style.transform = `rotate(${currentRotation}deg)`;
              else element.style.transform = `rotate(${currentRotation}deg)`;
            }
          }
        }

        animationFrameIdRef.current = requestAnimationFrame(animate);
      };

      if (animationStartDelayMs > 0) {
        animationStartTimeoutRef.current = setTimeout(() => {
          animationStartTimeoutRef.current = null;
          if (!isAnimatingRef.current) return;
          animationFrameIdRef.current = requestAnimationFrame(animate);
        }, animationStartDelayMs);
      } else {
        animationFrameIdRef.current = requestAnimationFrame(animate);
      }
    },
    [stopPlaybackAnimation]
  );

  const playFromSheet = useCallback(() => {
    setTimelineSheetOpen(false);
    // Ensure sheet closes so animation is visible on map.
    setTimeout(() => {
      if (mapInstanceRef.current) {
        isFullRouteRequestedRef.current = true;
        // Draw full route line (polyline) when user hits Play.
        const fn = setupPlaybackRouteRef.current;
        if (DEBUG_PLAYBACK_FIT && !fn) {
        }
        fn?.(mapInstanceRef.current, pointsRef.current, tripRef.current, {
          endpointsOnly: false,
        });
      }
      startPlaybackAnimation("fast");
    }, 0);
  }, [startPlaybackAnimation]);

  const removeEndpointMarkers = useCallback(() => {
    try {
      if (startMarkerRef.current && typeof startMarkerRef.current.remove === "function") {
        startMarkerRef.current.remove();
      }
    } catch (_) {}
    try {
      if (endMarkerRef.current && typeof endMarkerRef.current.remove === "function") {
        endMarkerRef.current.remove();
      }
    } catch (_) {}
    startMarkerRef.current = null;
    endMarkerRef.current = null;
  }, []);

  /** Start/End markers (default) + optional polyline. */
  const setupPlaybackRoute = useCallback(
    (map, pointsIn, tripIn, { endpointsOnly = true } = {}) => {
      if (!map || !pointsIn || pointsIn.length === 0) return;

      const tripData = tripIn ?? tripRef.current;
      const tripIdLabel =
        tripId ||
        tripData?.id ||
        tripData?._id ||
        tripData?.trip_id ||
        deviceId ||
        "N/A";

      const startRaw = pointsIn[0];
      const endRaw = pointsIn[pointsIn.length - 1];

      const startLL = pointLatLngNum(startRaw);
      const endLL = pointLatLngNum(endRaw);

      const fullPath = buildRouteCoordinates(pointsIn, tripData);
      const canEndpointOnly = Boolean(startLL && endLL);
      const fullRouteRequested = isFullRouteRequestedRef.current;
      const usingEndpointsOnly = endpointsOnly && canEndpointOnly && !fullRouteRequested;
      const viewportPath = usingEndpointsOnly ? [startLL, endLL] : fullPath;
      if (!viewportPath?.length) return;

      stopPlaybackAnimation();

      try {
        if (polylineRef.current && typeof polylineRef.current.remove === "function") {
          polylineRef.current.remove();
        }
      } catch (_) {}
      polylineRef.current = null;

      removeEndpointMarkers();

      const start = viewportPath[0];
      const end = viewportPath[viewportPath.length - 1];

      const startPoint = {
        ...startRaw,
        latitude: startRaw?.latitude ?? start.lat,
        longitude: startRaw?.longitude ?? start.lng,
        isStart: true,
      };
      const endPoint = {
        ...endRaw,
        latitude: endRaw?.latitude ?? end.lat,
        longitude: endRaw?.longitude ?? end.lng,
        isEnd: true,
      };

      // Always draw the route polyline (same behavior as MapplsMap.js),
      // but keep the camera fitted to endpoints-only by default.
      if (fullPath?.length >= 2) {
        try {
          if (DEBUG_PLAYBACK_FIT) {
            
          }
          polylineRef.current = new window.mappls.Polyline({
            map,
            path: fullPath,
            strokeColor: "#0000FF",
            strokeOpacity: 0.8,
            strokeWeight: 8,
            fitbounds: false,
            popupHtml: `
              <div style="padding: 8px;">
                <h3 style="margin: 0 0 4px 0; color: #1f2937; font-size: 14px; font-weight: 600;">Trip Route</h3>
                <p style="margin: 2px 0; font-size: 12px; color: #6b7280;"><strong>Total Points:</strong> ${pointsIn.length}</p>
                <p style="margin: 2px 0; font-size: 12px; color: #6b7280;"><strong>Duration:</strong> ${tripData?.duration_minutes ?? "N/A"} min</p>
                <p style="margin: 2px 0; font-size: 12px; color: #6b7280;"><strong>Distance:</strong> ${
                  tripData?.distance_km != null ? Number(tripData.distance_km).toFixed(2) : "N/A"
                } km</p>
              </div>
            `,
          });
        } catch (err) {
        }
      }

      try {
        startMarkerRef.current = new window.mappls.Marker({
          map,
          position: { lat: start.lat, lng: start.lng },
          icon: START_MARKER_ICON,
          width: 42,
          height: 42,
          fitbounds: false,
          popupHtml: buildEndpointPopupHtml({
            title: "🚀 Trip Start",
            titleColor: "#22c55e",
            point: startPoint,
            trip: tripData,
            tripIdLabel,
          }),
        });
        if (startMarkerRef.current?.addListener) {
          startMarkerRef.current.addListener("click", () => {
            try {
              if (map?.panTo) map.panTo({ lat: start.lat, lng: start.lng }, { duration: 800 });
            } catch (_) {}
          });
        }
      } catch (err) {
      }

      try {
        endMarkerRef.current = new window.mappls.Marker({
          map,
          position: { lat: end.lat, lng: end.lng },
          icon: END_MARKER_ICON,
          width: 42,
          height: 42,
          fitbounds: false,
          popupHtml: buildEndpointPopupHtml({
            title: "🏁 Trip End",
            titleColor: "#ef4444",
            point: endPoint,
            trip: tripData,
            tripIdLabel,
          }),
        });
        if (endMarkerRef.current?.addListener) {
          endMarkerRef.current.addListener("click", () => {
            try {
              if (map?.panTo) map.panTo({ lat: end.lat, lng: end.lng }, { duration: 800 });
            } catch (_) {}
          });
        }
      } catch (err) {
      }

      // Center/fit map on the endpoints (start/end) by default.
      fitPlaybackViewport(map, usingEndpointsOnly ? [start, end] : fullPath);
    },
    [tripId, deviceId, stopPlaybackAnimation, removeEndpointMarkers]
  );

  useLayoutEffect(() => {
    setupPlaybackRouteRef.current = setupPlaybackRoute;
  }, [setupPlaybackRoute]);

  useEffect(() => {
    const init = async () => {
      if (!deviceId) {
        toast.error("Missing device for playback");
        setIsLoading(false);
        return;
      }
      try {
        const list = await get(
          `/trips?device_id=${encodeURIComponent(
            deviceId
          )}&page=1&per_page=200&sortBy=trip_start_time&sortOrder=desc`
        );
        const trips = normalizeTripsList(list);
        setTripList(trips);

        let resolvedTripId = tripId;
        if (!resolvedTripId) {
          resolvedTripId = trips[0]?.id || trips[0]?._id || trips[0]?.trip_id;
        }
        if (!resolvedTripId) throw new Error("No trip found for playback");

        const response = await get(`/trips/${resolvedTripId}`);
        const tripData = response?.trip || null;
        const pts = Array.isArray(response?.points) ? response.points : [];
        if (!tripData || pts.length === 0) throw new Error("Playback points not found");

        const sorted = [...pts].sort(
          (a, b) => new Date(a.point_time) - new Date(b.point_time)
        );
        tripRef.current = tripData;
        setTrip(tripData);
        setPoints(sorted);
      } catch (err) {
        toast.error(err?.message || "Failed to load playback");
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [deviceId, tripId, get]);

  useEffect(() => {
    if (mapInstanceRef.current) return;

    let cancelled = false;
    let raf = 0;
    let loadFallbackTimer = null;
    let attempts = 0;
    let zoomPollInterval = null;

    const tryCreate = () => {
      if (cancelled) return;

      const el = mapRef.current;
      if (!el) {
        attempts += 1;
        if (attempts < 80) raf = requestAnimationFrame(tryCreate);
        return;
      }

      const { width, height } = el.getBoundingClientRect();
      if (width < 8 || height < 8) {
        attempts += 1;
        if (attempts < 160) raf = requestAnimationFrame(tryCreate);
        return;
      }

      (async () => {
        try {
          await waitForMapplsSdk();
        } catch {
          if (!cancelled) toast.error("Map SDK not loaded. Refresh the page.");
          return;
        }
        if (cancelled || mapInstanceRef.current) return;

        try {
          const map = new window.mappls.Map(el, {
            center: DEFAULT_CENTER,
            zoom: 12,
            zoomControl: true,
            location: false,
            scrollWheel: true,
            draggable: true,
            backgroundColor: "#f0f0f0",
          });
          mapInstanceRef.current = map;

          // Poll zoom so we can reliably log zoom-in/zoom-out even if Mappls
          // event names differ across SDK versions.
          if (DEBUG_USER_ZOOM_LOG) {
            let lastZoom = getMapZoomSafe(map);
            zoomPollInterval = setInterval(() => {
              const z = getMapZoomSafe(map);
              if (z == null) return;
              if (lastZoom == null) {
                lastZoom = z;
                return;
              }

              const delta = z - lastZoom;
              if (Math.abs(delta) >= 0.02) {
                
                lastZoom = z;
              }
            }, 250);
          }

          const runOverlays = () => {
            if (cancelled) return;
            if (loadFallbackTimer) {
              clearTimeout(loadFallbackTimer);
              loadFallbackTimer = null;
            }
            isMapLoadedRef.current = true;
            requestAnimationFrame(() => {
              setupPlaybackRoute(map, pointsRef.current, tripRef.current);
            });
          };

          if (typeof map.on === "function") {
            map.on("load", runOverlays);
            loadFallbackTimer = setTimeout(() => {
              if (!cancelled && !isMapLoadedRef.current) runOverlays();
            }, 2500);
          } else {
            setTimeout(runOverlays, 800);
          }
        } catch (e) {
          toast.error("Failed to initialize playback map");
        }
      })();
    };

    raf = requestAnimationFrame(tryCreate);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (loadFallbackTimer) clearTimeout(loadFallbackTimer);
      if (zoomPollInterval) clearInterval(zoomPollInterval);
      stopPlaybackAnimation();
      try {
        if (polylineRef.current && typeof polylineRef.current.remove === "function") {
          polylineRef.current.remove();
        }
      } catch (_) {}
      polylineRef.current = null;
      removeEndpointMarkers();
      // Do not set isMapLoadedRef false here: map instance can outlive this cleanup (Strict Mode);
      // overlays are removed above; next paint will redraw when points effect runs.
    };
  }, [stopPlaybackAnimation, setupPlaybackRoute, removeEndpointMarkers]);

  useEffect(() => {
    if (!mapInstanceRef.current || points.length === 0) return;
    const map = mapInstanceRef.current;
    const first = pointToLatLng(points[0]);
    if (!first) {
      toast.error("Trip points have no valid coordinates");
      return;
    }

    const mapReady = () => {
      if (!isMapLoadedRef.current) return false;
      try {
        if (typeof map.isStyleLoaded === "function") return map.isStyleLoaded();
      } catch (_) {}
      return true;
    };

    let tries = 0;
    const maxTries = 60;
    const tryDraw = () => {
      if (!mapInstanceRef.current) return;
      if (mapReady()) {
        setupPlaybackRoute(mapInstanceRef.current, points, trip);
        return;
      }
      tries += 1;
      if (tries < maxTries) {
        setTimeout(tryDraw, 100);
      } else {
        setupPlaybackRoute(mapInstanceRef.current, points, trip);
      }
    };

    tryDraw();
  }, [points, trip, setupPlaybackRoute]);

  useEffect(() => {
    if (!mapInstanceRef.current || points.length === 0) return;
    const map = mapInstanceRef.current;
    const t = setTimeout(() => {
      try {
        if (typeof map.resize === "function") map.resize();
        const first = points[0];
        const last = points[points.length - 1];
        // Keep camera centered on start/end only by default.
        const endpointsPath = buildRouteCoordinates([first, last], trip);
        if (endpointsPath.length) fitPlaybackViewport(map, endpointsPath);
      } catch (_) {}
    }, 320);
    return () => clearTimeout(t);
  }, [points, trip]);

  useEffect(() => {
    return () => {
      isFullRouteRequestedRef.current = false;
      stopPlaybackAnimation();
    };
  }, [stopPlaybackAnimation]);

  useEffect(() => {
    if (!timelineSheetOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [timelineSheetOpen]);

  useEffect(() => {
    if (!timelineSheetOpen) return;
    const t = setTimeout(() => {
      try {
        if (typeof mapInstanceRef.current?.resize === "function") {
          mapInstanceRef.current.resize();
        }
      } catch (_) {}
    }, 320);
    return () => clearTimeout(t);
  }, [timelineSheetOpen]);

  // When the bottom timeline sheet opens/closes, refit so the route stays visible
  // above the sheet (otherwise it can look clipped/partially hidden).
  useEffect(() => {
    if (!mapInstanceRef.current || points.length === 0) return;
    const map = mapInstanceRef.current;
    const t = setTimeout(() => {
      try {
        const first = points[0];
        const last = points[points.length - 1];
        const endpointsPath = buildRouteCoordinates([first, last], trip);
        if (endpointsPath.length) {
          fitPlaybackViewport(map, endpointsPath, { sheetOpen: timelineSheetOpen });
        }
      } catch (_) {}
    }, 450);
    return () => clearTimeout(t);
  }, [timelineSheetOpen, points, trip]);

  return (
    <div className="relative flex h-screen w-full flex-col bg-background">
      <div className="flex h-14 shrink-0 items-center border-b  text-foreground">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Playback History
        </Button>
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          className={`relative min-h-0 w-full flex-1 overflow-hidden ${
            timelineSheetOpen || calendarSheetOpen ? "playback-hide-mappls-controls" : ""
          }`}
        >
          <style>{`
            /* When any bottom sheet is open, hide Mappls built-in controls (3D, +/-) so they don’t clash. */
            .playback-hide-mappls-controls [class*="control"],
            .playback-hide-mappls-controls [class*="Control"],
            .playback-hide-mappls-controls [class*="zoom"],
            .playback-hide-mappls-controls [class*="Zoom"] {
              display: none !important;
              visibility: hidden !important;
              pointer-events: none !important;
            }
            /* If controls are fixed-position, lift them above the collapsed summary strip. */
            #playback-map-container [style*="position: fixed"] {
              bottom: 84px !important;
            }
          `}</style>
          <div className="absolute top-2 right-2 z-20 md:top-4 md:right-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setTimelineSheetOpen(false);
                setCalendarSheetOpen(true);
              }}
              className="bg-white dark:bg-white text-gray-900 border border-gray-200 shadow-md hover:bg-white hover:text-gray-900 h-9 w-9 p-0 touch-manipulation"
              title="All Trips"
              aria-label="All Trips"
            >
              <CalendarDays className="h-4 w-4 text-gray-700" />
            </Button>
          </div>

          <div
            id="playback-map-container"
            ref={mapRef}
            className="h-full w-full min-h-[240px]"
          />
        </div>

        {!isLoading && points.length > 0 && (
          <button
            type="button"
            id="playback-timeline-summary"
            onClick={() => setTimelineSheetOpen(true)}
            className="relative z-30 -mt-14 mb-2 flex min-h-[7.5rem] w-full min-w-0 shrink-0 items-center gap-4 rounded-t-2xl border-1 border-muted-foreground/20 bg-card/95 px-4 py-4 text-left shadow-lg ring-1 ring-black/5 backdrop-blur-md transition-colors hover:bg-card dark:bg-card/90 dark:ring-white/10 sm:-mt-16 sm:min-h-[8rem] sm:px-5 sm:py-5"
            aria-label="Open trip timeline"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-semibold text-foreground">{tripTitle}</p>
              {tripSubtitle ? (
                <p className="mt-1 truncate text-sm text-muted-foreground">{tripSubtitle}</p>
              ) : null}
              <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm tabular-nums text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
                  {tripRuntimeLabel}
                </span>
                <span className="text-border" aria-hidden>
                  ·
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Navigation className="h-4 w-4 shrink-0 text-muted-foreground" />
                  {distanceLabel}
                </span>
                <span className="text-border" aria-hidden>
                  ·
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Gauge className="h-4 w-4 shrink-0 text-muted-foreground" />
                  {avgDistancePerDayLabel}
                </span>
              </div>
            </div>
            <div className="flex h-full min-h-[5.5rem] shrink-0 flex-col items-center justify-center gap-1 self-stretch rounded-xl border-1 border-muted-foreground/20 bg-muted px-4 py-3 text-foreground sm:min-h-[6rem] sm:px-5">
              <ChevronUp className="h-6 w-6" aria-hidden />
              <span className="max-w-[5rem] text-center text-[11px] font-semibold uppercase tracking-wide">
                Timeline
              </span>
            </div>
          </button>
        )}

        {timelineSheetOpen && (
          <>
            <button
              type="button"
              className="fixed inset-0 z-40 bg-black/50"
              aria-label="Close timeline"
              onClick={() => setTimelineSheetOpen(false)}
            />
            <div
              className="fixed inset-x-0 bottom-0 z-50 flex max-h-[min(92dvh,calc(100vh-0.5rem))] flex-col overflow-hidden rounded-t-[1.25rem] border-1 border-muted-foreground/20 bg-background shadow-[0_-12px_48px_-16px_rgba(0,0,0,0.18)] dark:bg-card dark:shadow-[0_-12px_48px_-16px_rgba(0,0,0,0.45)] pb-[max(0.5rem,env(safe-area-inset-bottom))]"
              role="dialog"
              aria-modal="true"
              aria-labelledby="playback-timeline-title"
            >
              <div className="shrink-0 flex flex-col items-center px-3 pt-2 pb-1" aria-hidden>
                <div className="h-1 w-10 shrink-0 rounded-full bg-muted-foreground/30" />
              </div>
              <div className="flex shrink-0 items-center justify-between gap-2 px-3 pb-2 pt-1">
                <div className="min-w-0 py-1">
                  <h2
                    id="playback-timeline-title"
                    className="truncate text-base font-semibold text-foreground "
                  >
                    Trip timeline
                  </h2>
                  <p className="truncate text-xs text-muted-foreground">{tripTitle}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  onClick={() => setTimelineSheetOpen(false)}
                  aria-label="Close"
                >
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                </Button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-4 pt-2 sm:pb-6 sm:pt-3">
                <div className="space-y-3">
                  <div className="hidden h-1.5 w-full overflow-hidden rounded-full bg-muted sm:block">
                    <div className="h-full w-full rounded-full bg-primary/70" />
                  </div>
                  <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center">
                    <Button
                      type="button"
                      size="sm"
                      className="w-full rounded-full bg-primary px-6 text-primary-foreground hover:bg-primary/90 sm:w-auto"
                      onClick={() => {
                        if (isAnimatingRef.current) {
                          isFullRouteRequestedRef.current = false;
                          stopPlaybackAnimation();
                        }
                        else playFromSheet();
                      }}
                    >
                      {isAnimating ? (
                        <>
                          <Pause className="mr-2 h-4 w-4" />
                          Stop
                        </>
                      ) : (
                        <>
                          <Play className="mr-2 h-4 w-4" />
                          Play
                        </>
                      )}
                    </Button>
                    <div className="flex w-full items-center justify-center text-foreground rounded-full border-1 border-muted-foreground/20 bg-muted/40 px-0.5 sm:w-auto sm:justify-start">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        onClick={() =>
                          setPlaybackSpeed((s) =>
                            Math.max(0.5, Math.round((s - 0.5) * 10) / 10)
                          )
                        }
                        aria-label="Slower playback"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="min-w-[2.75rem] text-center text-sm font-semibold tabular-nums">
                        {playbackSpeed}x
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        onClick={() =>
                          setPlaybackSpeed((s) =>
                            Math.min(2, Math.round((s + 0.5) * 10) / 10)
                          )
                        }
                        aria-label="Faster playback"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 rounded-2xl border-1 border-muted-foreground/20 bg-muted/30 px-2 py-2.5 text-center dark:bg-muted/15 sm:gap-2 sm:rounded-xl sm:py-3">
                    <div className="flex flex-col items-center gap-0.5 sm:gap-1">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-[10px] font-medium  uppercase tracking-wide text-muted-foreground">
                        Runtime
                      </span>
                      <span className="text-xs font-semibold tabular-nums text-foreground">{tripRuntimeLabel}</span>
                    </div>
                    <div className="flex flex-col items-center gap-0.5 border-x border-border sm:gap-1">
                      <Navigation className="h-4 w-4 text-muted-foreground" />
                      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Distance
                      </span>
                      <span className="text-xs font-semibold tabular-nums text-foreground">{distanceLabel}</span>
                    </div>
                    <div className="flex flex-col items-center gap-0.5 sm:gap-1">
                      <Gauge className="h-4 w-4 text-muted-foreground" />
                      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Avg/day
                      </span>
                      <span className="text-xs font-semibold tabular-nums text-foreground">{avgDistancePerDayLabel}</span>
                    </div>
                  </div>
                  {distanceByDay.length > 0 && (
                    <div className="rounded-2xl border-1 border-muted-foreground/20 bg-muted/25 px-3 py-2.5 dark:bg-muted/10 sm:rounded-xl">
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Distance by day
                      </p>
                      <ul className="space-y-1.5">
                        {distanceByDay.map((row) => (
                          <li
                            key={row.dateKey}
                            className="flex items-center justify-between gap-2 text-sm"
                          >
                            <span className="text-foreground">{row.label}</span>
                            <span className="shrink-0 font-semibold tabular-nums text-foreground">
                              {row.km < 0.001
                                ? `${(row.km * 1000).toFixed(0)} m`
                                : `${row.km.toFixed(3)} km`}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="mt-5 border-t border-border/60 pt-4 sm:mt-6">
                  {!isLoading && points.length === 0 && (
                    <p className="py-8 text-center text-sm text-muted-foreground">No timeline points for this trip.</p>
                  )}
                  {!isLoading && tripCards.length > 0 && (
                    <div className="relative mx-auto max-w-lg pb-6 sm:pb-8">
                      <div
                        className="absolute bottom-6 left-[13px] top-6 w-0.5 rounded-full bg-primary/45 sm:left-[15px] sm:w-1 sm:bg-primary/70"
                        aria-hidden
                      />
                      <ul className="relative space-y-0">
                        {tripCards.map((seg, segIdx) => {
                          const isExpanded = Boolean(expandedTrips[seg.tripId]);
                          return (
                            <li key={seg.tripId} className="relative pb-3 pl-9 sm:pl-10">
                              <div className="group mb-2 rounded-2xl border-1 border-muted-foreground/20 bg-card px-3 py-3 shadow-sm transition-all hover:border-primary/35 hover:shadow-md sm:rounded-xl sm:bg-gradient-to-r sm:from-card sm:to-muted/40 sm:shadow-md sm:ring-1 sm:ring-black/[0.05] dark:sm:from-card/80 dark:sm:to-muted/20 dark:sm:ring-white/5">
                                <button
                                  type="button"
                                  onClick={() => toggleTripExpand(seg, isExpanded)}
                                  className="flex w-full min-w-0 items-center justify-between gap-3 text-left"
                                  aria-expanded={isExpanded}
                                  aria-label={`Toggle trip ${seg.tripNum}`}
                                >
                                  <div className="flex min-w-0 items-start gap-2.5 ">
                                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-sky-500/20 to-emerald-500/15 text-sky-700 ring-1 ring-sky-500/25 dark:text-sky-300 dark:ring-sky-400/30">
                                      <Route className="h-4 w-4" aria-hidden />
                                    </span>
                                    <div className="min-w-0">
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground ">
                                        Trip {seg.tripNum}
                                      </p>
                                      <p className="mt-0.5 text-sm font-medium text-foreground">
                                        {formatTimeAmPm(seg.startTime)} → {formatTimeAmPm(seg.endTime)}
                                      </p>
                                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                                        {seg.isActive
                                          ? `${seg.points.length} point${seg.points.length === 1 ? "" : "s"}`
                                          : "Tap Map to load route"}
                                      </p>
                                    </div>
                                  </div>
                                  {isExpanded ? (
                                    <ChevronUp className="h-5 w-5 shrink-0 text-sky-600 dark:text-sky-400" />
                                  ) : (
                                    <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground transition group-hover:text-foreground " />
                                  )}
                                </button>
                                <div className="mt-3 flex justify-stretch sm:mt-2.5 sm:justify-end">
                                  <button
                                    type="button"
                                    className="w-full rounded-xl border-1 border-muted-foreground/20 bg-primary/5 py-2.5 text-sm font-semibold text-primary transition-colors active:bg-primary/10 sm:w-auto sm:border-0 sm:bg-transparent sm:py-0 sm:text-blue-700 sm:underline-offset-4 sm:hover:underline dark:sm:text-blue-300"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      loadTripOnMap(tripList[segIdx]);
                                    }}
                                    title={`View Trip ${seg.tripNum} on map`}
                                    aria-label={`View Trip ${seg.tripNum} on map`}
                                  >
                                    View on map
                                  </button>
                                </div>
                              </div>

                              <div
                                className={`grid transition-all duration-300 ease-in-out ${
                                  isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                                }`}
                              >
                                <div className="overflow-hidden">
                                  {isExpanded && seg.isLoadingPoints && seg.points.length === 0 && (
                                    <div className="px-3 pb-2 text-xs text-muted-foreground">
                                      Loading timeline points...
                                    </div>
                                  )}
                                  {seg.points.map((p, pi) => {
                                    const kind = p.kind;
                                    return (
                                      <div
                                        key={`${seg.tripId}-${pi}`}
                                        className="relative pb-6 pl-0 last:pb-2"
                                      >
                                        <div
                                          className={`absolute top-1.5 ${
                                            kind === "start" || kind === "end"
                                              ? "-left-9 flex h-7 w-7 items-center justify-center rounded-full border-1 border-muted-foreground/20 shadow-md ring-2 ring-background sm:-left-10 sm:h-8 sm:w-8 sm:border-1 sm:border-muted-foreground/10"
                                              : "-left-[22px] h-3.5 w-3.5 rounded-full border-2 border-background shadow-sm sm:-left-[27px] sm:h-4 sm:w-4"
                                          } ${
                                            kind === "start"
                                              ? "border-sky-200 bg-gradient-to-br from-sky-400 to-sky-600"
                                              : kind === "end"
                                                ? "border-emerald-200 bg-gradient-to-br from-emerald-500 to-emerald-700"
                                                : "bg-muted ring-2 ring-muted-foreground/25"
                                          }`}
                                          aria-hidden
                                        >
                                          {kind === "end" ? <MapPin className="h-4 w-4 text-white" /> : null}
                                        </div>

                                        <div className="rounded-2xl border-1 border-muted-foreground/20 bg-muted/20 p-3.5 dark:bg-background/50 sm:rounded-xl sm:border-1 sm:border-muted-foreground/10 sm:bg-background sm:p-3 sm:shadow-sm dark:sm:border-white/10 dark:sm:bg-background/40">
                                          <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                                {p.label}
                                              </p>
                                              <p className="mt-1 font-mono text-[13px] font-semibold leading-snug text-foreground sm:text-sm">
                                                {formatCoordAddress(p.lat, p.lng)}
                                              </p>
                                              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                                                {formatTimeAmPm(p.time)}
                                                <span className="text-border"> · </span>
                                                Trip {seg.tripNum}
                                              </p>
                                              <p className="mt-1.5 text-[11px] text-muted-foreground">
                                                Distance: {formatKmToHuman(p.distanceKm ?? 0)}
                                              </p>
                                            </div>
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="icon"
                                              className="shrink-0 rounded-full text-muted-foreground hover:bg-primary/10 hover:text-primary"
                                              onClick={() => openLatLngDetails(p.lat, p.lng)}
                                              aria-label="Show location"
                                            >
                                              <ChevronRight className="h-5 w-5" />
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {!seg.isActive && !seg.isLoadingPoints && seg.points.length === 0 && (
                                    <div className="px-3 pb-2 text-xs text-muted-foreground">
                                      Load this trip on map to view its timeline points.
                                    </div>
                                  )}
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Mobile: Calendar bottom sheet */}
        <Sheet open={calendarSheetOpen} onOpenChange={setCalendarSheetOpen}>
          <SheetContent
            side="bottom"
            className="p-0 border-t border-border bg-background"
          >
            <div className="px-4 pt-3 pb-4">
              <SheetHeader className="px-0">
                <SheetTitle className="text-base">Select Date</SheetTitle>
                <SheetDescription>
                  Choose a date to view locations for this day.
                </SheetDescription>
              </SheetHeader>

              <div className="mt-3 rounded-2xl border border-muted-foreground/10 bg-muted/10 dark:bg-[#181818] p-2">
                <CalendarPicker
                  mode="single"
                  selected={selectedCalendarDate}
                  disabled={disableDaysAfterToday}
                  endMonth={new Date()}
                  classNames={{
                    caption_label: "text-sm font-medium !text-foreground",
                    nav_button:
                      "h-7 w-7 bg-transparent !text-foreground p-0 opacity-70 hover:opacity-100 transition",
                    /** v9: only `weekday` applies to Mo–Su labels; do not set `weekdays` to flex (breaks table row layout). */
                    weekday:
                      "rounded-md w-9 font-normal text-[0.8rem] !text-muted-foreground",
                    day: "h-9 w-9 p-0 font-normal !text-foreground aria-selected:opacity-100 hover:bg-muted rounded-md transition-colors",
                    day_selected: "bg-muted !text-foreground",
                    day_today: "bg-muted !text-foreground",
                    day_outside: "!text-foreground opacity-50",
                    day_disabled: "!text-foreground opacity-50",
                  }}
                  components={{
                    DayButton: (btnProps) => {
                      const date = btnProps?.day?.date;
                      const d = date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
                      const key = d
                        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
                            d.getDate()
                          ).padStart(2, "0")}`
                        : null;
                      const km = key ? tripDistanceByDayMap.get(key) : null;

                      return (
                        <button {...btnProps}>
                          <span className="flex h-9 w-9 flex-col items-center justify-center leading-none">
                            <span className="text-[12px]">{d ? d.getDate() : ""}</span>
                            {typeof km === "number" && Number.isFinite(km) && km > 0 ? (
                              <span className="mt-0.5 text-[9px] font-semibold text-muted-foreground">
                                {km.toFixed(km < 10 ? 1 : 0)}km
                              </span>
                            ) : null}
                          </span>
                        </button>
                      );
                    },
                  }}
                  onSelect={(d) => {
                    if (!d) return;
                    setSelectedCalendarDate(d);
                    setCalendarSheetOpen(false);
                    const dayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
                      d.getDate()
                    ).padStart(2, "0")}`;
                    navigate(
                      `${mapBasePath}/map/day-playback/${encodeURIComponent(
                        deviceId
                      )}?date=${encodeURIComponent(dayKey)}`
                    );
                  }}
                />
              </div>

              <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground px-1">
                <span>
                  Selected:{" "}
                  <span className="font-medium text-foreground">
                    {selectedCalendarDate?.toLocaleDateString(undefined, {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </span>

                <button
                  type="button"
                  className="text-foreground/80 hover:text-foreground font-medium"
                  onClick={() => setCalendarSheetOpen(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {isLoading && (
        <div className="pointer-events-none absolute inset-0 top-14 flex items-center justify-center bg-background/40">
          <p className="text-sm text-muted-foreground">Loading…</p>
        </div>
      )}
    </div>
  );
}
